import type { FulfillmentOrderLine, FulfillmentSupplierOption } from "../types";
import type { EntryExistingDraft, EntryParseResult, EntryPreviewItem } from "./confirmationEditorEntry";
import { changedEntryFields } from "./confirmationEditorEntry";
import { calculateFulfillmentLineAmount, confirmationTravelerRoleLabel, validateFulfillmentLineDraft } from "./fulfillmentOrderEditorService";
import { confirmationDecimalInput } from "./confirmationDecimalDisplay";

export type AirEntryInput = {
  mode?: "air" | "ship";
  text: string;
  existing: FulfillmentOrderLine[];
  roleCounts?: Partial<Record<string, number>>;
  provider?: FulfillmentSupplierOption | null;
  settlement?: Pick<FulfillmentSupplierOption, "id" | "name"> | null;
};
export type AirEntryResult = {
  items: Array<EntryPreviewItem & { original?: FulfillmentOrderLine }>;
  errors: EntryParseResult["errors"];
  drafts: EntryExistingDraft[];
  additions: Partial<FulfillmentOrderLine>[];
};

const roles = "学生|老师|教师|导游|司机|成人|儿童";
const routeKey = (value: string) => value.normalize("NFKC").trim().replace(/[‐‑–—−]/g, "-").replace(/\s*([-→])\s*/g, "$1");
const identity = (line: Partial<FulfillmentOrderLine>) => JSON.stringify([routeKey(line.itemNameSnapshot || ""), confirmationTravelerRoleLabel(line.travelerRole)]);
const amounts = (line: Partial<FulfillmentOrderLine>) => JSON.stringify([line.quantity, line.billingCount, line.unitPriceAmount].map(value => confirmationDecimalInput(value || "")));

/** 原明细文本只表达路线、角色与量价，日期和主体留在原行快照中。 */
export function formatAirEntryLines(lines: FulfillmentOrderLine[]): string {
  return lines.map(line => `${line.itemNameSnapshot} ${confirmationTravelerRoleLabel(line.travelerRole) || "待填角色"}${confirmationDecimalInput(line.quantity)}×${confirmationDecimalInput(line.billingCount)}×${confirmationDecimalInput(line.unitPriceAmount)}`).join("\n");
}

/** 解析人工飞机文本，并按本次明确范围维护原行身份。 */
export function parseAirEntry(input: AirEntryInput): AirEntryResult {
  const mode = input.mode ?? "air";
  const label = mode === "ship" ? "船票" : "飞机";
  const exampleRoute = mode === "ship" ? "大连 - 烟台" : "重庆 - 北京";
  const result: AirEntryResult = { items: [], errors: [], drafts: [], additions: [] };
  const parsed: EntryPreviewItem[] = [];
  for (const source of input.text.matchAll(/[^\n;；]+/g)) {
    let route = "", offset = source.index;
    for (const raw of source[0].split(/[,，、]/)) {
      const location = { raw, sourceStart: offset, sourceEnd: offset + raw.length };
      offset += raw.length + 1;
      if (!raw.trim()) continue;
      try {
        const text = raw.trim();
        const withRoute = text.match(new RegExp(`^(.+?)\\s+(${roles})\\s*(.+)$`));
        const person = withRoute || text.match(new RegExp(`^(${roles})\\s*(.+)$`));
        if (!person) throw new Error(`请填写路线、角色与票价，例如${exampleRoute} 老师3×1×1100`);
        if (withRoute) route = withRoute[1].trim();
        if (!route) throw new Error(`请先填写本行路线，例如${exampleRoute}`);
        const role = (withRoute ? withRoute[2] : person[1]).replace("教师", "老师");
        const formula = (withRoute ? withRoute[3] : person[2]).normalize("NFKC").replace(/[xX*]/g, "×");
        const factors = formula.split("×").map(part => part.trim());
        if (factors.length > 3 || factors.some(part => !part)) throw new Error("请填写人数×次数×单价，最多三个计费因素");
        const values = factors.map((part, index) => {
          const last = index === factors.length - 1;
          const match = part.match(last ? /^(\d+(?:\.\d{1,2})?)\s*元?$/ : index === 0 ? /^(\d+)\s*人?$/ : /^(\d+)\s*次?$/);
          if (!match) throw new Error("人数、次数须为整数，单价须为非负数字且最多两位小数");
          return match[1];
        });
        const quantity = values.length === 1 ? input.roleCounts?.[role] : values[0];
        if (quantity == null || !Number.isSafeInteger(Number(quantity)) || Number(quantity) < 0 || (values.length === 1 && Number(quantity) <= 0)) throw new Error(`没有可确认的${role}人数，请明确填写实际人数`);
        const billingCount = values.length === 3 ? values[1] : "1";
        if (Number(billingCount) <= 0) throw new Error("次数必须大于零");
        const price = values.at(-1)!;
        const amount = calculateFulfillmentLineAmount(String(quantity), billingCount, price);
        const patch: Partial<FulfillmentOrderLine> = {
          resourceType: "大交通", transportMode: mode, procurementSourceType: `manual_${mode}`, procurementSourceId: null,
          itemNameSnapshot: route, itemSpecSnapshot: null, travelerRole: role, quantity: String(quantity), billingCount,
          unitPriceAmount: price, amount, unit: "人", pricingUnitType: "unit", sourceType: "manual", status: "active", reviewStatus: "confirmed",
          resourceSelection: null, spuId: null, spuTableName: null, skuId: null, skuTableName: null,
          dayExpression: "", dayNumbers: [], serviceDates: [], serviceDateFrom: null, serviceDateTo: null, dateMappingStatus: "mapped",
          remark: `${label}录入：${raw}${Number(amount) === 0 ? " 免费" : ""}`,
        };
        const errors = validateFulfillmentLineDraft({ ...patch, id: "existing-preview" } as FulfillmentOrderLine);
        if (errors.length) throw new Error(errors.join("；"));
        parsed.push({ ...location, key: String(location.sourceStart), title: route, formula, patch, notices: [] });
      } catch (error) {
        result.errors.push({ ...location, message: error instanceof Error ? error.message : "请核对路线与票价", field: "formula" });
      }
    }
  }
  const used = new Set<string>();
  for (const item of parsed) {
    const candidates = input.existing.filter(line => identity(line) === identity(item.patch));
    const related = parsed.filter(entry => identity(entry.patch) === identity(item.patch));
    if (candidates.length > 1 && (related.length !== candidates.length || related.some((entry, index) => amounts(entry.patch) !== amounts(candidates[index])))) {
      result.errors.push({ ...item, message: "存在同路线、同角色的多笔费用，无法唯一确认原行；请只勾选目标行后重新编辑", field: "formula" });
      continue;
    }
    const original = candidates.find(line => !used.has(line.id)) || (input.existing.length === 1 && parsed.length === 1 ? input.existing[0] : undefined);
    try {
      if (input.provider && !input.provider.canBeFulfillmentSubject) throw new Error("所选服务主体不具备履约资格，请重新选择");
      if (input.provider && input.provider.type !== "composite_supplier") throw new Error(`${label}服务主体须选择可履约的综合服务公司`);
      const subjects: Partial<FulfillmentOrderLine> = {
        ...(input.provider !== undefined ? { fulfillmentSupplierId: input.provider?.id ?? null, fulfillmentSupplierName: input.provider?.name ?? null } : {}),
        ...(input.settlement !== undefined && !original?.fulfillmentOrderId ? { settlementSupplierId: input.settlement?.id ?? null, settlementSupplierName: input.settlement?.name ?? null } : {}),
      };
      if (original) {
        used.add(original.id);
        const sameRoute = routeKey(original.itemNameSnapshot) === routeKey(item.patch.itemNameSnapshot!);
        const patch = changedEntryFields({ base: original, removed: false, patch: {
          quantity: item.patch.quantity, billingCount: item.patch.billingCount, unitPriceAmount: item.patch.unitPriceAmount,
          ...(confirmationTravelerRoleLabel(original.travelerRole) !== item.patch.travelerRole ? { travelerRole: item.patch.travelerRole } : {}),
          ...(!sameRoute ? { itemNameSnapshot: item.patch.itemNameSnapshot, resourceSelection: null, spuId: null, skuId: null, spuTableName: null, skuTableName: null, spuNameSnapshot: null, skuNameSnapshot: null, procurementSourceType: `manual_${mode}`, procurementSourceId: null, transportMode: mode } : {}),
          ...subjects,
        } });
        const next = { ...original, ...patch };
        if (["quantity", "billingCount", "unitPriceAmount"].some(field => field in patch)) next.amount = calculateFulfillmentLineAmount(next.quantity, next.billingCount, next.unitPriceAmount);
        result.drafts.push({ base: original, patch, removed: false });
        result.items.push({ ...item, original, patch: next });
      } else {
        if (Number(item.patch.quantity) <= 0) throw new Error("新增费用人数必须大于零；零数量只保留已有费用");
        const patch = { ...item.patch, ...subjects };
        result.additions.push(patch);
        result.items.push({ ...item, patch });
      }
    } catch (error) {
      result.errors.push({ ...item, message: error instanceof Error ? error.message : "请核对费用", field: "formula" });
    }
  }
  const unmatched = input.existing.filter(line => !used.has(line.id));
  if (unmatched.length && result.additions.length) {
    const addition = result.items.find(item => !item.original)!;
    result.errors.push({ ...addition, message: "本次多行范围同时出现旧路线消失与新路线，无法确认原行身份；请只勾选目标行修改路线，独立新增或删除请分次应用", field: "formula" });
  }
  result.drafts.push(...unmatched.map(base => ({ base, patch: {}, removed: true })));
  return result;
}
